import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { buildPlanningMapMarkers, normalizeActivitySummary, snowflakeId } from '../journey-planning/planningModel';

const count = (value) => Math.max(0, Number(value) || 0);

function parseObject(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { const value = JSON.parse(raw); return value && typeof value === 'object' ? value : {}; }
  catch { return {}; }
}

/** 與後端 Boolean 對齊：缺省／畸形視為不可複製（產品預設 OFF）。 */
function normalizeAllowCopy(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export function normalizePublicJourney(value) {
  const destination = parseObject(value?.destinationSummaryJson);
  const itinerary = normalizePublishedItinerary(value);
  const frozen = itinerary.days.length > 0 || itinerary.unscheduledActivities.length > 0;
  return {
    publicationId: value?.publicationId == null ? null : String(value.publicationId),
    creatorMemberId: value?.creatorMemberId == null ? null : String(value.creatorMemberId),
    title: value?.title || 'Shared journey',
    summary: value?.summary || '',
    destination: destination.displayName || destination.name || destination.summary || '',
    startDate: value?.startDate || null,
    endDate: value?.endDate || null,
    coverUrl: resolveMemberMediaUrl(value?.coverFileReference),
    dayCount: frozen ? itinerary.days.length : 0,
    activityCount: frozen
      ? itinerary.days.reduce((total, day) => total + day.activities.length, 0) + itinerary.unscheduledActivities.length
      : 0,
    publishedAt: value?.publishedAt || null,
    allowCopy: normalizeAllowCopy(value?.allowCopy),
    visibility: String(value?.visibility || '').toUpperCase(),
    itinerary,
    itineraryUnavailable: !frozen,
    story: normalizePublicStory(value?.story),
  };
}

export const PUBLIC_JOURNEY_VIEWS = Object.freeze(['overview', 'story']);

/** 無參數或無效值一律回 overview，保留舊連結行為。 */
export function resolvePublicJourneyView(raw) {
  return raw === 'story' ? 'story' : 'overview';
}

const STORY_AUTHOR_ROLES = new Set(['OWNER', 'COMPANION']);

/**
 * 卡片／媒體 ID 只接受字串或安全整數；超出安全範圍的 Number 代表精度已遺失，視為無 ID，不嘗試補救。
 */
function storyCardId(value) {
  if (typeof value === 'string') return /^[1-9]\d*$/.test(value) ? value : null;
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return String(value);
  return null;
}

const text = (value) => (typeof value === 'string' ? value : '');

/** 舊快照缺 media → []；mediaId 全程字串，不經 Number。 */
export function normalizeStoryMediaList(value) {
  return (Array.isArray(value) ? value : [])
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => ({
      mediaId: storyCardId(item.mediaId),
      displayOrder: Number.isFinite(Number(item.displayOrder)) ? Number(item.displayOrder) : index,
      mediaType: item.mediaType == null || item.mediaType === '' || item.mediaType === 'IMAGE'
        ? 'IMAGE'
        : null,
    }))
    .filter((item) => item.mediaId && item.mediaType === 'IMAGE')
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * 公開故事（CARD_V1）。available=false＝此版本尚無故事；available=true 且 cards=[]＝已發布但無可公開卡片。
 * 只保留文字、類型、時間、作者角色與 media allowlist；不保留任何會員 ID、objectKey 或未知欄位。
 */
export function normalizePublicStory(value) {
  if (!value || typeof value !== 'object' || value.available !== true) {
    return { available: false, cards: [] };
  }
  const cards = (Array.isArray(value.cards) ? value.cards : [])
    .filter((card) => card && typeof card === 'object')
    .map((card, index) => {
      const media = normalizeStoryMediaList(card.media);
      return {
        cardId: storyCardId(card.cardId),
        authorRole: STORY_AUTHOR_ROLES.has(card.authorRole) ? card.authorRole : '',
        memoryType: text(card.memoryType),
        title: text(card.title),
        content: text(card.content),
        occurredAt: text(card.occurredAt) || null,
        displayOrder: Number.isFinite(Number(card.displayOrder)) ? Number(card.displayOrder) : index,
        media,
      };
    })
    .filter((card) => card.title || card.content || card.media.length > 0)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  return { available: true, cards };
}

/** 瀏覽器安全的 idempotency key；單次使用者 copy intent 應持有同一值。 */
export const COPY_CLIENT_REQUEST_ID_MAX = 64;

export function createJourneyCopyClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, COPY_CLIENT_REQUEST_ID_MAX);
  }
  return `travel-copy-${Date.now()}-${Math.random().toString(36).slice(2)}`
    .slice(0, COPY_CLIENT_REQUEST_ID_MAX);
}

export function normalizeCopyJourneyResult(value) {
  const journeyId = value?.journeyId == null || value.journeyId === ''
    ? null
    : String(value.journeyId);
  return {
    journeyId,
    replay: Boolean(value?.replay),
  };
}

export function normalizePublishedItinerary(value) {
  const source = value?.itinerary && typeof value.itinerary === 'object' ? value.itinerary : {};
  const days = Array.isArray(source.days) ? source.days.map(normalizePublishedDay) : [];
  const unscheduledActivities = Array.isArray(source.unscheduledActivities)
    ? source.unscheduledActivities.map(normalizePublishedActivity)
    : [];
  const markers = buildPlanningMapMarkers({
    days: days.map((day) => ({ ...day, displayOrder: day.dayNumber })),
    unscheduledActivities,
  });
  const mapSummary = source.mapSummary || value?.mapSummary || {};
  return {
    days,
    unscheduledActivities,
    markers,
    mapSummary: {
      markerCount: count(mapSummary.markerCount) || markers.length,
      hasCoordinates: Boolean(mapSummary.hasCoordinates) || markers.length > 0,
    },
  };
}

function normalizePublishedDay(value) {
  const activities = Array.isArray(value?.activities)
    ? value.activities.map(normalizePublishedActivity)
    : [];
  return {
    dayId: snowflakeId(value?.dayId),
    dayNumber: Number(value?.dayNumber) || 0,
    date: value?.date || null,
    title: value?.title || '',
    activities,
  };
}

function normalizePublishedActivity(value) {
  return normalizeActivitySummary({
    ...value,
    plannedStartTime: value?.startTime || value?.plannedStartTime,
    plannedEndTime: value?.endTime || value?.plannedEndTime,
  });
}

export function normalizeTimelineItem(value) {
  const snapshot = parseObject(value?.displaySnapshotJson);
  const location = parseObject(snapshot.locationSummaryJson);
  return {
    timelineItemId: value?.timelineItemId,
    type: value?.itemType || 'ACTIVITY',
    sortTime: value?.sortTime || null,
    dayIndex: value?.dayIndex == null ? null : Number(value.dayIndex),
    title: snapshot.title || (value?.itemType === 'MEMORY' ? 'Travel memory' : 'Activity'),
    content: snapshot.content || '',
    activityType: snapshot.activityType || '',
    memoryType: snapshot.memoryType || '',
    location: location.displayName || location.name || location.summary || '',
    plannedStartTime: snapshot.plannedStartTime || null,
    plannedEndTime: snapshot.plannedEndTime || null,
  };
}

export function normalizeTimelinePage(value) {
  return {
    items: Array.isArray(value?.items) ? value.items.map(normalizeTimelineItem) : [],
    hasMore: Boolean(value?.hasMore),
    nextCursor: value?.hasMore && value?.nextCursorSortTime && value?.nextCursorTimelineItemId ? {
      cursorSortTime: value.nextCursorSortTime,
      cursorTimelineItemId: value.nextCursorTimelineItemId,
    } : null,
  };
}

export function normalizeJourneyEngagement(value) {
  return { liked: Boolean(value?.liked), saved: Boolean(value?.saved), likeCount: count(value?.likeCount), saveCount: count(value?.saveCount) };
}
